import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Trash2,
  Plus,
  FileText,
  RefreshCw,
  Zap,
  Target,
  Edit,
  X,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useDojoSettings } from "@/hooks/useDojoSettings";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface Requirement {
  id: string;
  title: string;
  description: string;
  category: string;
}

interface BeltLevel {
  id: string;
  name: string;
  color: string;
  order: number;
  requirements: Requirement[];
}

// Comprehensive Martial Arts Database
const MARTIAL_ARTS_DATABASE = {
  Karate: {
    styles: {
      Kyokushin: {
        belts: [
          "No Kyu (White Belt)",
          "10th Kyu (Orange Belt)",
          "9th Kyu (Orange Belt with Black Stripe)",
          "8th Kyu (Blue Belt)",
          "7th Kyu (Blue Belt with Black Stripe)",
          "6th Kyu (Yellow Belt)",
          "5th Kyu (Yellow Belt with Black Stripe)",
          "4th Kyu (Green Belt)",
          "3rd Kyu (Green Belt with Black Stripe)",
          "2nd Kyu (Brown Belt)",
          "1st Kyu (Brown Belt with Black Stripe)",
          "1st Dan (Black Belt)",
          "2nd Dan (Black Belt)",
          "3rd Dan (Black Belt)",
          "4th Dan (Black Belt)",
        ],
        categories: [
          "Etiquette",
          "Stances",
          "Strikes",
          "Blocks",
          "Kicks",
          "Kata",
          "Kumite",
          "Fitness",
        ],
      },
      Shotokan: {
        belts: [
          "10th Kyu (White Belt)",
          "9th Kyu (Yellow Belt)",
          "8th Kyu (Orange Belt)",
          "7th Kyu (Green Belt)",
          "6th Kyu (Blue Belt)",
          "5th Kyu (Purple Belt)",
          "4th Kyu (Purple Belt with White Stripe)",
          "3rd Kyu (Brown Belt)",
          "2nd Kyu (Brown Belt)",
          "1st Kyu (Brown Belt)",
          "1st Dan (Black Belt)",
          "2nd Dan (Black Belt)",
          "3rd Dan (Black Belt)",
          "4th Dan (Black Belt)",
        ],
        categories: ["Kihon", "Kata", "Kumite", "Terminology", "Philosophy"],
      },
      "Goju-Ryu": {
        belts: [
          "10th Kyu (White Belt)",
          "9th Kyu (Yellow Belt)",
          "8th Kyu (Orange Belt)",
          "7th Kyu (Green Belt)",
          "6th Kyu (Blue Belt)",
          "5th Kyu (Purple Belt)",
          "4th Kyu (Brown Belt)",
          "3rd Kyu (Brown Belt)",
          "2nd Kyu (Brown Belt)",
          "1st Kyu (Brown Belt)",
          "1st Dan (Black Belt)",
          "2nd Dan (Black Belt)",
          "3rd Dan (Black Belt)",
          "4th Dan (Black Belt)",
        ],
        categories: ["Kihon", "Kata", "Kumite", "Breathing", "Philosophy"],
      },
      "Wado-Ryu": {
        belts: [
          "9th Kyu (Red Belt)",
          "8th Kyu (Yellow Belt)",
          "7th Kyu (Orange Belt)",
          "6th Kyu (Green Belt)",
          "5th Kyu (Blue Belt)",
          "4th Kyu (Purple Belt)",
          "3rd Kyu (Brown Belt)",
          "2nd Kyu (Brown Belt)",
          "1st Kyu (Brown Belt)",
          "1st Dan (Black Belt)",
          "2nd Dan (Black Belt)",
          "3rd Dan (Black Belt)",
          "4th Dan (Black Belt)",
        ],
        categories: ["Kihon", "Kata", "Kumite", "Ohyo", "Philosophy"],
      },
    },
  },
  Taekwondo: {
    styles: {
      "WTF/WT (World Taekwondo)": {
        belts: [
          "10th Gup (White Belt)",
          "9th Gup (White Belt with Yellow Stripe)",
          "8th Gup (Yellow Belt)",
          "7th Gup (Yellow Belt with Green Stripe)",
          "6th Gup (Green Belt)",
          "5th Gup (Green Belt with Blue Stripe)",
          "4th Gup (Blue Belt)",
          "3rd Gup (Blue Belt with Red Stripe)",
          "2nd Gup (Red Belt)",
          "1st Gup (Red Belt with Black Stripe)",
          "1st Dan (Black Belt)",
          "2nd Dan (Black Belt)",
          "3rd Dan (Black Belt)",
          "4th Dan (Black Belt)",
        ],
        categories: [
          "Poomsae",
          "Kicking",
          "Blocking",
          "Striking",
          "Sparring",
          "Self-Defense",
          "Philosophy",
        ],
      },
      "ITF (International Taekwon-Do Federation)": {
        belts: [
          "10th Gup (White Belt)",
          "9th Gup (White Belt with Yellow Tag)",
          "8th Gup (Yellow Belt)",
          "7th Gup (Yellow Belt with Green Tag)",
          "6th Gup (Green Belt)",
          "5th Gup (Green Belt with Blue Tag)",
          "4th Gup (Blue Belt)",
          "3rd Gup (Blue Belt with Red Tag)",
          "2nd Gup (Red Belt)",
          "1st Gup (Red Belt with Black Tag)",
          "1st Dan (Black Belt)",
          "2nd Dan (Black Belt)",
          "3rd Dan (Black Belt)",
          "4th Dan (Black Belt)",
        ],
        categories: [
          "Patterns",
          "Sparring",
          "Self-Defense",
          "Breaking",
          "Theory",
          "Fundamental Movements",
        ],
      },
      "ATA (American Taekwondo Association)": {
        belts: [
          "White Belt",
          "Orange Belt",
          "Yellow Belt",
          "Camouflage Belt",
          "Green Belt",
          "Purple Belt",
          "Blue Belt",
          "Brown Belt",
          "Red Belt",
          "1st Dan (Black Belt)",
          "2nd Dan (Black Belt)",
          "3rd Dan (Black Belt)",
          "4th Dan (Black Belt)",
        ],
        categories: [
          "Forms",
          "One-Steps",
          "Sparring",
          "Board Breaking",
          "Weapons",
          "Leadership",
        ],
      },
    },
  },
  Judo: {
    styles: {
      "Kodokan Judo": {
        belts: [
          "6th Kyu (White Belt)",
          "5th Kyu (Yellow Belt)",
          "4th Kyu (Orange Belt)",
          "3rd Kyu (Green Belt)",
          "2nd Kyu (Blue Belt)",
          "1st Kyu (Brown Belt)",
          "1st Dan (Black Belt)",
          "2nd Dan (Black Belt)",
          "3rd Dan (Black Belt)",
          "4th Dan (Black Belt)",
        ],
        categories: [
          "Nage-waza",
          "Katame-waza",
          "Ukemi",
          "Randori",
          "Kata",
          "Theory",
        ],
      },
    },
  },

  "Jiu-Jitsu": {
    styles: {
      "Japanese Jiu-Jitsu": {
        belts: [
          "White",
          "Yellow",
          "Orange",
          "Green",
          "Blue",
          "Brown",
          "Black",
          "2nd Dan",
          "3rd Dan",
          "4th Dan",
        ],
        categories: [
          "Kata",
          "Striking",
          "Joint Locks",
          "Throws",
          "Ground Control",
          "Defense Against Weapons",
          "Traditional Forms",
          "Self-Defense Techniques",
          "Breakfalls",
          "Randori",
        ],
      },
    },
  },
  "Brazilian Jiu-Jitsu (BJJ)": {
    styles: {
      "IBJJF (International Brazilian Jiu-Jitsu Federation)": {
        belts: [
          "White Belt",
          "Blue Belt",
          "Purple Belt",
          "Brown Belt",
          "Black Belt",
          "Coral Belt (7th Degree)",
          "Coral Belt (8th Degree)",
          "Red Belt (9th Degree)",
          "Red Belt (10th Degree)",
        ],
        categories: [
          "Guard",
          "Escapes",
          "Submissions",
          "Takedowns",
          "Positions",
          "Defense",
          "Transitions",
        ],
      },
      "Gracie Barra": {
        belts: [
          "White Belt",
          "Blue Belt",
          "Purple Belt",
          "Brown Belt",
          "Black Belt",
          "Coral Belt",
          "Red Belt",
        ],
        categories: [
          "Self-Defense",
          "Sport BJJ",
          "No-Gi",
          "Takedowns",
          "Guard",
          "Submissions",
          "Philosophy",
        ],
      },
      "Alliance Jiu-Jitsu": {
        belts: [
          "White Belt",
          "Blue Belt",
          "Purple Belt",
          "Brown Belt",
          "Black Belt",
          "Coral Belt",
          "Red Belt",
        ],
        categories: [
          "Fundamentals",
          "Advanced Techniques",
          "Competition",
          "Self-Defense",
          "No-Gi",
          "MMA Applications",
        ],
      },
      "Atos Jiu-Jitsu": {
        belts: [
          "White Belt",
          "Blue Belt",
          "Purple Belt",
          "Brown Belt",
          "Black Belt",
          "Coral Belt",
          "Red Belt",
        ],
        categories: [
          "Modern BJJ",
          "Sport Techniques",
          "Leg Locks",
          "Guard Systems",
          "Pressure Passing",
          "Competition",
        ],
      },
      CheckMat: {
        belts: [
          "White Belt",
          "Blue Belt",
          "Purple Belt",
          "Brown Belt",
          "Black Belt",
          "Coral Belt",
          "Red Belt",
        ],
        categories: [
          "Traditional BJJ",
          "Self-Defense",
          "Vale Tudo",
          "Competition",
          "Fundamentals",
          "Advanced",
        ],
      },
      "10th Planet Jiu-Jitsu": {
        belts: [
          "White Belt",
          "Blue Belt",
          "Purple Belt",
          "Brown Belt",
          "Black Belt",
        ],
        categories: [
          "No-Gi",
          "Rubber Guard",
          "Lockdown",
          "Truck System",
          "Leg Locks",
          "Modern Techniques",
        ],
      },
      "Carlson Gracie Team": {
        belts: [
          "White Belt",
          "Blue Belt",
          "Purple Belt",
          "Brown Belt",
          "Black Belt",
          "Coral Belt",
          "Red Belt",
        ],
        categories: [
          "Vale Tudo",
          "Competition",
          "Self-Defense",
          "Conditioning",
          "Aggressive Style",
          "MMA",
        ],
      },
      "Nova União": {
        belts: [
          "White Belt",
          "Blue Belt",
          "Purple Belt",
          "Brown Belt",
          "Black Belt",
          "Coral Belt",
          "Red Belt",
        ],
        categories: [
          "Competition",
          "Technical BJJ",
          "Guard Development",
          "Submission Chains",
          "Flow Rolling",
        ],
      },
      GFTeam: {
        belts: [
          "White Belt",
          "Blue Belt",
          "Purple Belt",
          "Brown Belt",
          "Black Belt",
          "Coral Belt",
          "Red Belt",
        ],
        categories: [
          "Competition",
          "Self-Defense",
          "MMA",
          "Fundamentals",
          "Advanced Techniques",
          "Team Training",
        ],
      },
    },
  },

  MMA: {
    styles: {
      "Unified MMA": {
        belts: [
          "Beginner (White)",
          "Novice (Grey)",
          "Intermediate (Yellow)",
          "Advanced (Orange)",
          "Expert (Green)",
          "Elite (Blue)",
          "Brown",
          "Black Level 1",
          "Black Level 2",
          "Black Level 3",
        ],
        categories: [
          "Striking (Boxing, Kickboxing)",
          "Grappling (Wrestling, BJJ)",
          "Takedowns",
          "Takedown Defense",
          "Submissions",
          "Submission Defense",
          "Ground and Pound",
          "Cage Work",
          "Conditioning",
          "Sparring",
        ],
      },
      "Brazilian MMA Style": {
        belts: [
          "White",
          "Blue",
          "Purple",
          "Brown",
          "Black",
          "Black Level 2",
          "Black Level 3",
        ],
        categories: [
          "Muay Thai",
          "BJJ for MMA",
          "Clinch Control",
          "Transitions",
          "Striking to Takedown",
          "Defensive Grappling",
          "Submission Chains",
          "Escape Techniques",
          "Sparring (Full Contact)",
          "Fight IQ / Strategy",
        ],
      },
    },
  },

  Kickboxing: {
    styles: {
      "American Kickboxing": {
        belts: [
          "Beginner (White)",
          "Intermediate (Yellow)",
          "Advanced (Orange)",
          "Expert (Green)",
          "Assistant Instructor (Blue)",
          "Instructor (Brown)",
          "Black Belt 1st Dan",
          "Black Belt 2nd Dan",
          "Black Belt 3rd Dan",
        ],
        categories: [
          "Footwork",
          "Punches",
          "Kicks",
          "Combinations",
          "Defense",
          "Sparring",
          "Conditioning",
        ],
      },
    },
  },

  "Muay Thai": {
    styles: {
      "Traditional Muay Thai": {
        belts: [
          "White Prajioud",
          "Yellow Prajioud",
          "Orange Prajioud",
          "Green Prajioud",
          "Blue Prajioud",
          "Purple Prajioud",
          "Brown Prajioud",
          "Red Prajioud",
          "Black Prajioud",
        ],
        categories: [
          "Punches",
          "Kicks",
          "Knees",
          "Elbows",
          "Clinch",
          "Defense",
          "Conditioning",
        ],
      },
    },
  },
  "Kung Fu": {
    styles: {
      "Wing Chun": {
        belts: [
          "Student Level 1",
          "Student Level 2",
          "Student Level 3",
          "Practitioner Level 1",
          "Practitioner Level 2",
          "Practitioner Level 3",
          "Instructor Level 1",
          "Instructor Level 2",
          "Master Level",
        ],
        categories: [
          "Forms",
          "Chi Sau",
          "Applications",
          "Weapons",
          "Philosophy",
          "Sensitivity Training",
        ],
      },
      Shaolin: {
        belts: [
          "White Sash",
          "Yellow Sash",
          "Orange Sash",
          "Green Sash",
          "Blue Sash",
          "Purple Sash",
          "Brown Sash",
          "Black Sash 1st Degree",
          "Black Sash 2nd Degree",
        ],
        categories: [
          "Basic Forms",
          "Animal Forms",
          "Weapons",
          "Applications",
          "Qigong",
          "Philosophy",
        ],
      },
    },
  },
  Aikido: {
    styles: {
      Aikikai: {
        belts: [
          "6th Kyu (White Belt)",
          "5th Kyu (Yellow Belt)",
          "4th Kyu (Orange Belt)",
          "3rd Kyu (Green Belt)",
          "2nd Kyu (Blue Belt)",
          "1st Kyu (Brown Belt)",
          "1st Dan (Black Belt)",
          "2nd Dan (Black Belt)",
          "3rd Dan (Black Belt)",
          "4th Dan (Black Belt)",
        ],
        categories: [
          "Basic Techniques",
          "Weapons",
          "Multiple Attackers",
          "Ukemi",
          "Philosophy",
          "Ki Development",
        ],
      },
    },
  },
  "Krav Maga": {
    styles: {
      "IKMF (International Krav Maga Federation)": {
        belts: [
          "Practitioner 1 (Yellow)",
          "Practitioner 2 (Orange)",
          "Practitioner 3 (Green)",
          "Practitioner 4 (Blue)",
          "Practitioner 5 (Brown)",
          "Graduate 1 (Black)",
          "Graduate 2 (Black)",
          "Graduate 3 (Black)",
          "Expert 1 (Black)",
          "Expert 2 (Black)",
        ],
        categories: [
          "Striking",
          "Self-Defense",
          "Ground Fighting",
          "Weapons Defense",
          "Scenario Training",
          "Fitness",
        ],
      },
    },
  },
};

const RefactoredAdminSyllabusManager = () => {
  useUiLanguage();
  const { t } = useTranslation(["admin", "common", "forms"]);
  const { profile } = useAuth();
  const { settings } = useDojoSettings();

  const [beltLevels, setBeltLevels] = useState<BeltLevel[]>([]);
  const [markdownInput, setMarkdownInput] = useState("");
  const [selectedMartialArt, setSelectedMartialArt] = useState("");
  const [selectedStyle, setSelectedStyle] = useState("");
  const [selectedBelt, setSelectedBelt] = useState<string>("");
  const [editingBelt, setEditingBelt] = useState<string | null>(null);
  const [newRequirement, setNewRequirement] = useState<Omit<Requirement, "id">>(
    {
      title: "",
      description: "",
      category: "",
    },
  );
  const [isLoading, setIsLoading] = useState(false);

  // Get available styles for selected martial art
  const getAvailableStyles = () => {
    if (!selectedMartialArt || !MARTIAL_ARTS_DATABASE[selectedMartialArt]) {
      return [];
    }
    return Object.keys(MARTIAL_ARTS_DATABASE[selectedMartialArt].styles);
  };

  // Get belt system for selected martial art and style
  const getBeltSystem = () => {
    if (
      !selectedMartialArt ||
      !selectedStyle ||
      !MARTIAL_ARTS_DATABASE[selectedMartialArt]?.styles[selectedStyle]
    ) {
      return null;
    }
    return MARTIAL_ARTS_DATABASE[selectedMartialArt].styles[selectedStyle];
  };

  // Get belt color based on name - FIXED with vibrant colors
  const getBeltColor = (beltName: string): string => {
    const name = beltName.toLowerCase();

    if (name.includes("white")) return "bg-white text-gray-900 border-gray-400";
    if (name.includes("yellow")) return "bg-yellow-500 text-gray-900";
    if (name.includes("orange")) return "bg-orange-600 text-white";
    if (name.includes("blue")) return "bg-blue-600 text-white";
    if (name.includes("green")) return "bg-green-600 text-white";
    if (name.includes("purple")) return "bg-purple-600 text-white";
    if (name.includes("brown")) return "bg-amber-800 text-white";
    if (name.includes("red")) return "bg-red-600 text-white";
    if (name.includes("black")) return "bg-gray-900 text-white";
    if (name.includes("camouflage")) return "bg-green-700 text-white";

    return "bg-gray-600 text-white";
  };

  // Create martial art system
  const createMartialArtSystem = async () => {
    if (!selectedMartialArt || !selectedStyle) {
      toast({
        title: uiText("Error"),
        description: uiText("Please select both martial art and style."),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const beltSystem = getBeltSystem();
      if (!beltSystem) {
        throw new Error(uiText("Belt system not found"));
      }

      console.log("🔄 Starting belt system creation...");

      // Step 1: NUCLEAR CLEAR - Clear ALL belt references globally first
      console.log("🧹 NUCLEAR: Clearing ALL belt references globally...");

      const { error: globalClearError } = await supabase
        .from("profiles")
        .update({ belt_level_id: null })
        .eq("dojo_id", profile.dojo_id);

      if (globalClearError) {
        console.error("Error in global clear:", globalClearError);
        throw globalClearError;
      }

      console.log("✅ ALL profile belt references cleared for this dojo");

      // Step 2: Wait for database to process
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Step 3: Delete ALL belt levels for this dojo
      console.log("🗑️ Deleting ALL belt levels for this dojo...");

      const { error: deleteError } = await supabase
        .from("belt_levels")
        .delete()
        .eq("dojo_id", profile.dojo_id);

      if (deleteError) {
        console.error("Error deleting existing belts:", deleteError);
        throw deleteError;
      }

      console.log("✅ ALL belt levels cleared for this dojo");

      // Step 4: Wait longer to ensure complete cleanup
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Step 5: Create new belt levels
      console.log(`🎯 Creating ${beltSystem.belts.length} new belt levels...`);

      for (let i = 0; i < beltSystem.belts.length; i++) {
        const beltName = beltSystem.belts[i];

        console.log(
          `📝 Creating belt ${i + 1}/${beltSystem.belts.length}: ${beltName}`,
        );

        const { error } = await supabase.from("belt_levels").insert({
          dojo_id: profile.dojo_id,
          name: beltName,
          color: getBeltColor(beltName),
          order_sequence: i,
          requirements: [], // Start with empty requirements
        });

        if (error) {
          console.error(`Error creating belt level "${beltName}":`, error);
          throw error;
        }

        console.log(`✅ Created: ${beltName}`);
      }

      console.log("🎉 Belt system creation complete!");

      toast({
        title: uiText("Success!"),
        description: uiText("Created {{value0}} {{value1}} belt system with {{value2}} levels.", { value0: selectedMartialArt, value1: selectedStyle, value2: beltSystem.belts.length }),
      });

      setSelectedMartialArt("");
      setSelectedStyle("");

      // Notify other components to refresh
      window.dispatchEvent(new CustomEvent("beltSystemChanged"));

      fetchBeltLevels();
    } catch (error) {
      console.error("Error creating martial art system:", error);

      // Provide specific error message based on error type
      let errorMessage = uiText("Failed to create belt system.");
      if (error.code === "23505") {
        errorMessage =
          uiText("Database constraint error - some belt names may already exist. Try using 'Clean & Reorder' first to remove duplicates.");
      } else if (error.code === "23503") {
        errorMessage =
          uiText("Cannot create belt system - students are still assigned to existing belts. Use 'Delete All' first.");
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        title: uiText("Error"),
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Parse markdown - FIXED VERSION for your format
  const parseMarkdownSyllabus = (markdown: string) => {
    console.log("🔍 Starting to parse markdown...");

    // Split by belt level headers - match "## No Kyu", "## 10th Kyu", etc.
    const sections = markdown.split(
      /(?=^##\s+(?:No|\d+(?:st|nd|rd|th))\s+(?:Kyu|Dan))/gm,
    );
    const belts: any[] = [];

    console.log(`📋 Found ${sections.length} potential sections`);

    sections.forEach((section, index) => {
      const trimmedSection = section.trim();
      if (!trimmedSection || trimmedSection.startsWith("---")) return;

      console.log(
        `🔍 Processing section ${index + 1}:`,
        trimmedSection.substring(0, 100),
      );

      const lines = trimmedSection
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line);
      if (lines.length === 0) return;

      // Extract belt name from first line - format: "## 10th Kyu – ORANGE BELT"
      let beltName = lines[0]
        .replace(/^##\s+/, "") // Remove "## "
        .trim();

      if (!beltName || beltName.length < 3 || beltName === "---") {
        console.warn(
          `⚠️ Skipping section with invalid belt name: "${beltName}"`,
        );
        return;
      }

      console.log(`🥋 Processing belt: "${beltName}"`);

      const requirements: any[] = [];
      let currentCategory = "General"; // Default category

      lines.slice(1).forEach((line, lineIndex) => {
        // Skip empty lines and dividers
        if (!line || line === "---") return;

        // Check if this is a category header (### Category)
        if (line.startsWith("### ")) {
          currentCategory = line.replace("### ", "").trim();
          console.log(`📂 Found category: "${currentCategory}"`);
          return;
        }

        // Process requirement lines (start with -)
        if (line.startsWith("- ")) {
          let requirementText = line.replace(/^-\s+/, "").trim();

          if (requirementText.length > 2) {
            // Split title and description if there's a dash
            const parts = requirementText.split(" – ");
            const title = parts[0].trim();
            const description =
              parts.length > 1 ? parts.slice(1).join(" – ").trim() : "";

            requirements.push({
              id: `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              title: title,
              description: description,
              category: currentCategory,
            });

            console.log(
              `✅ Added requirement: "${title}" to category "${currentCategory}"`,
            );
          }
        }
      });

      if (requirements.length > 0) {
        belts.push({
          name: beltName,
          color: getBeltColor(beltName),
          order: index,
          requirements: requirements,
        });

        console.log(
          `🎯 Created belt "${beltName}" with ${requirements.length} requirements`,
        );
      } else {
        console.warn(`⚠️ No requirements found for belt: "${beltName}"`);
      }
    });

    console.log(`🎉 Parsing complete! Created ${belts.length} belt levels`);
    return belts;
  };

  // Handle markdown upload
  const handleMarkdownUpload = async () => {
    if (!profile?.dojo_id || !markdownInput.trim()) {
      toast({
        title: uiText("Error"),
        description: uiText("Please enter markdown content."),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const parsedBelts = parseMarkdownSyllabus(markdownInput);

      if (parsedBelts.length === 0) {
        toast({
          title: uiText("No Belt Levels Found"),
          description: uiText("Could not parse any belt levels from the markdown."),
          variant: "destructive",
        });
        return;
      }

      console.log("🔄 Starting markdown import...");

      // Step 1: Get all existing belt IDs for this dojo
      const { data: existingBelts, error: fetchError } = await supabase
        .from("belt_levels")
        .select("id")
        .eq("dojo_id", profile.dojo_id);

      if (fetchError) {
        console.error("Error fetching existing belts:", fetchError);
        throw fetchError;
      }

      const beltIds = existingBelts?.map((belt) => belt.id) || [];
      console.log(`📊 Found ${beltIds.length} existing belt levels to clear`);

      // Step 2: Clear ALL profile references to these specific belt IDs
      if (beltIds.length > 0) {
        console.log("🧹 Clearing profile references...");

        const { error: profileError } = await supabase
          .from("profiles")
          .update({ belt_level_id: null })
          .in("belt_level_id", beltIds);

        if (profileError) {
          console.error("Error clearing profile references:", profileError);
          throw profileError;
        }

        console.log("✅ Profile references cleared");
      }

      // Step 3: Delete existing belt levels for this dojo
      console.log("🗑️ Deleting existing belt levels...");

      const { error: deleteError } = await supabase
        .from("belt_levels")
        .delete()
        .eq("dojo_id", profile.dojo_id);

      if (deleteError) {
        console.error("Error deleting existing belts:", deleteError);
        throw deleteError;
      }

      console.log("✅ Existing belts cleared");

      // Step 4: Wait a moment to ensure deletion is complete
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Step 5: Create new belt levels from parsed markdown
      console.log(
        `🎯 Creating ${parsedBelts.length} belt levels from markdown...`,
      );

      for (let i = 0; i < parsedBelts.length; i++) {
        const belt = parsedBelts[i];

        console.log(
          `📝 Creating belt ${i + 1}/${parsedBelts.length}: ${belt.name}`,
        );

        const { error } = await supabase.from("belt_levels").insert({
          dojo_id: profile.dojo_id,
          name: belt.name,
          color: belt.color,
          order_sequence: i,
          requirements: belt.requirements,
        });

        if (error) {
          console.error(`Error creating belt level "${belt.name}":`, error);
          throw error;
        }

        console.log(
          `✅ Created: ${belt.name} with ${belt.requirements.length} requirements`,
        );
      }

      console.log("🎉 Markdown import complete!");

      toast({
        title: uiText("Success!"),
        description: uiText("Created {{value0}} belt levels from markdown.", { value0: parsedBelts.length }),
      });

      setMarkdownInput("");

      // Notify other components to refresh
      window.dispatchEvent(new CustomEvent("beltSystemChanged"));

      fetchBeltLevels();
    } catch (error) {
      console.error("Error processing markdown:", error);

      // Provide specific error message based on error type
      let errorMessage = uiText("Failed to process markdown content.");
      if (error.code === "23505") {
        errorMessage =
          uiText("Database constraint error - some belt names may already exist. Try using 'Clean & Reorder' first to remove duplicates.");
      } else if (error.code === "23503") {
        errorMessage =
          uiText("Cannot import - students are still assigned to existing belts. Use 'Delete All' first.");
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        title: uiText("Error"),
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Clean up and reorder belt system
  const cleanupBeltSystem = async () => {
    if (!profile?.dojo_id) return;

    setIsLoading(true);

    try {
      const { data: belts, error: fetchError } = await supabase
        .from("belt_levels")
        .select("*")
        .eq("dojo_id", profile.dojo_id);

      if (fetchError) throw fetchError;

      // Remove duplicates and sort
      const uniqueBelts = belts.reduce((acc, belt) => {
        const existing = acc.find(
          (b) => b.name.toLowerCase().trim() === belt.name.toLowerCase().trim(),
        );
        if (!existing) {
          acc.push(belt);
        }
        return acc;
      }, []);

      // Sort belts in proper order
      uniqueBelts.sort((a, b) => {
        const getOrder = (name) => {
          const lower = name.toLowerCase();
          if (lower.includes("kyu") || lower.includes("gup")) {
            const num = parseInt(lower.match(/(\d+)/)?.[1] || "0");
            return 1000 - num;
          } else if (lower.includes("dan") || lower.includes("degree")) {
            const num = parseInt(lower.match(/(\d+)/)?.[1] || "1");
            return 2000 + num;
          }
          return 3000;
        };
        return getOrder(a.name) - getOrder(b.name);
      });

      // Clear and re-insert
      await supabase
        .from("profiles")
        .update({ belt_level_id: null })
        .eq("dojo_id", profile.dojo_id);

      await supabase
        .from("belt_levels")
        .delete()
        .eq("dojo_id", profile.dojo_id);

      for (let i = 0; i < uniqueBelts.length; i++) {
        const belt = uniqueBelts[i];
        await supabase.from("belt_levels").insert({
          dojo_id: profile.dojo_id,
          name: belt.name,
          color: belt.color,
          order_sequence: i,
          requirements: belt.requirements || [],
        });
      }

      toast({
        title: uiText("System Cleaned"),
        description: uiText("Removed duplicates and reordered {{value0}} belt levels.", { value0: uniqueBelts.length }),
      });

      // Notify other components to refresh
      window.dispatchEvent(new CustomEvent("beltSystemChanged"));

      fetchBeltLevels();
    } catch (error) {
      console.error("Error cleaning belt system:", error);
      toast({
        title: uiText("Cleanup Failed"),
        description: uiText("Failed to clean up the belt system."),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Force reset - handles constraint issues
  const forceResetBeltSystem = async () => {
    if (!profile?.dojo_id) return;

    const confirmed = window.confirm(
      uiText("🚨 FORCE RESET DATABASE\n\nThis will forcibly clear ALL belt-related data including:\n• All belt levels\n• All student belt assignments\n• All progress data\n\nUse this if you're getting constraint errors.\n\nContinue?"),
    );

    if (!confirmed) return;

    setIsLoading(true);

    try {
      console.log("🚨 Starting NUCLEAR force reset...");

      // Step 1: Get all belt level IDs for this dojo
      console.log("📊 Getting belt level IDs for this dojo...");
      const { data: beltData, error: beltFetchError } = await supabase
        .from("belt_levels")
        .select("id")
        .eq("dojo_id", profile.dojo_id);

      if (beltFetchError) {
        console.error("Error fetching belt IDs:", beltFetchError);
        throw beltFetchError;
      }

      const beltIds = beltData?.map((belt) => belt.id) || [];
      console.log(`📋 Found ${beltIds.length} belt levels to clear:`, beltIds);

      if (beltIds.length > 0) {
        // Step 2: Check which profiles have these belt IDs
        console.log("🔍 Checking which profiles have these belt IDs...");
        const { data: profilesWithBelts, error: profileCheckError } =
          await supabase
            .from("profiles")
            .select("id, first_name, last_name, belt_level_id")
            .in("belt_level_id", beltIds);

        if (profileCheckError) {
          console.error("Error checking profiles:", profileCheckError);
        } else {
          console.log(
            `📋 Found ${profilesWithBelts?.length || 0} profiles with these belt IDs:`,
            profilesWithBelts,
          );
        }

        // Step 3: NUCLEAR APPROACH - Clear ALL belt references globally
        console.log("🧹 NUCLEAR: Clearing ALL belt references globally...");

        const { error: globalClearError } = await supabase
          .from("profiles")
          .update({ belt_level_id: null })
          .not("belt_level_id", "is", null);

        if (globalClearError) {
          console.error("Error in global clear:", globalClearError);
          throw globalClearError;
        }

        console.log("✅ ALL profile belt references cleared globally");

        // Step 4: Wait longer for database to process
        console.log("⏳ Waiting 5 seconds for database to process...");
        await new Promise((resolve) => setTimeout(resolve, 5000));

        // Step 5: Double-check that references are actually cleared
        console.log("🔍 Double-checking that references are cleared...");
        const { data: remainingRefs, error: checkError } = await supabase
          .from("profiles")
          .select("id, belt_level_id")
          .in("belt_level_id", beltIds);

        if (checkError) {
          console.error("Error checking remaining references:", checkError);
        } else {
          console.log(
            `📊 Remaining references: ${remainingRefs?.length || 0}`,
            remainingRefs,
          );

          if (remainingRefs && remainingRefs.length > 0) {
            console.warn(
              "⚠️ Still found references, trying individual updates...",
            );

            for (const ref of remainingRefs) {
              console.log(
                `🔧 Manually clearing reference for profile ${ref.id}`,
              );
              await supabase
                .from("profiles")
                .update({ belt_level_id: null })
                .eq("id", ref.id);
            }

            // Wait again
            await new Promise((resolve) => setTimeout(resolve, 2000));
          }
        }
      }

      // Step 6: Try to delete belt levels
      console.log("🗑️ Attempting to delete belt levels...");
      const { error: deleteError } = await supabase
        .from("belt_levels")
        .delete()
        .eq("dojo_id", profile.dojo_id);

      if (deleteError) {
        console.error("❌ STILL getting delete error:", deleteError);

        // Last resort: Try deleting by individual IDs
        console.log(
          "🚨 LAST RESORT: Trying to delete individual belt levels...",
        );
        for (const beltId of beltIds) {
          console.log(`🗑️ Deleting belt ID: ${beltId}`);
          const { error: individualDeleteError } = await supabase
            .from("belt_levels")
            .delete()
            .eq("id", beltId);

          if (individualDeleteError) {
            console.error(
              `❌ Failed to delete belt ${beltId}:`,
              individualDeleteError,
            );
          } else {
            console.log(`✅ Successfully deleted belt ${beltId}`);
          }
        }
      } else {
        console.log("✅ Belt levels deleted successfully");
      }

      // Step 7: Final cleanup
      setBeltLevels([]);
      setSelectedBelt("");

      // Notify other components
      window.dispatchEvent(new CustomEvent("beltSystemChanged"));

      toast({
        title: uiText("Nuclear Reset Complete"),
        description: uiText("Performed aggressive cleanup. Check console for details."),
      });

      console.log("🎉 Nuclear reset completed!");
    } catch (error) {
      console.error("💥 Nuclear reset failed:", error);
      toast({
        title: uiText("Nuclear Reset Failed"),
        description: uiText("Even the nuclear option failed: {{value0}}", { value0: error.message }),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Delete individual belt
  const handleDeleteBeltLevel = async (beltId: string, beltName: string) => {
    const confirmed = window.confirm(
      uiText("Delete \"{{value0}}\"?\n\nThis will permanently remove this belt level.", { value0: beltName }),
    );
    if (!confirmed) return;

    setIsLoading(true);

    try {
      // Clear profile references first
      await supabase
        .from("profiles")
        .update({ belt_level_id: null })
        .eq("belt_level_id", beltId);

      // Delete the belt level
      await supabase.from("belt_levels").delete().eq("id", beltId);

      setBeltLevels((prev) => prev.filter((belt) => belt.id !== beltId));

      if (selectedBelt === beltId) {
        setSelectedBelt("");
      }

      // Notify other components to refresh
      window.dispatchEvent(new CustomEvent("beltSystemChanged"));

      toast({
        title: uiText("Belt Deleted"),
        description: uiText("\"{{value0}}\" has been deleted.", { value0: beltName }),
      });
    } catch (error) {
      console.error("Error deleting belt level:", error);
      toast({
        title: uiText("Delete Failed"),
        description: uiText("Failed to delete the belt level."),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch belt levels
  const fetchBeltLevels = async () => {
    if (!profile?.dojo_id) return;

    try {
      const { data, error } = await supabase
        .from("belt_levels")
        .select("*")
        .eq("dojo_id", profile.dojo_id)
        .order("order_sequence");

      if (error) {
        console.error("Error fetching belt levels:", error);
        return;
      }

      const convertedBeltLevels = (data || []).map((dbBelt) => ({
        id: dbBelt.id,
        name: dbBelt.name,
        color: dbBelt.color || "bg-gray-100",
        order: dbBelt.order_sequence,
        requirements: Array.isArray(dbBelt.requirements)
          ? dbBelt.requirements
          : [],
      }));

      setBeltLevels(convertedBeltLevels);
    } catch (error) {
      console.error("Error fetching belt levels:", error);
    }
  };

  useEffect(() => {
    fetchBeltLevels();
  }, [profile?.dojo_id]);

  // Add requirement to belt
  const handleAddRequirement = async () => {
    if (!selectedBelt || !newRequirement.title || !newRequirement.category) {
      toast({
        title: uiText("Error"),
        description: uiText("Please fill in all required fields."),
        variant: "destructive",
      });
      return;
    }

    const requirement: Requirement = {
      ...newRequirement,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
    };

    try {
      const currentBelt = beltLevels.find((belt) => belt.id === selectedBelt);
      if (!currentBelt) return;

      const updatedRequirements = [...currentBelt.requirements, requirement];

      const { error } = await supabase
        .from("belt_levels")
        .update({ requirements: updatedRequirements })
        .eq("id", selectedBelt);

      if (error) throw error;

      setBeltLevels((prev) =>
        prev.map((belt) =>
          belt.id === selectedBelt
            ? { ...belt, requirements: updatedRequirements }
            : belt,
        ),
      );

      setNewRequirement({ title: "", description: "", category: "" });

      // Notify other components to refresh
      window.dispatchEvent(new CustomEvent("beltSystemChanged"));

      toast({
        title: uiText("Requirement Added"),
        description: uiText("Requirement has been saved successfully"),
      });
    } catch (error) {
      console.error("Error adding requirement:", error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to add requirement."),
        variant: "destructive",
      });
    }
  };

  // Delete requirement
  const handleDeleteRequirement = async (
    beltId: string,
    requirementId: string,
  ) => {
    try {
      const currentBelt = beltLevels.find((belt) => belt.id === beltId);
      if (!currentBelt) return;

      const updatedRequirements = currentBelt.requirements.filter(
        (r) => r.id !== requirementId,
      );

      const { error } = await supabase
        .from("belt_levels")
        .update({ requirements: updatedRequirements })
        .eq("id", beltId);

      if (error) throw error;

      setBeltLevels((prev) =>
        prev.map((belt) =>
          belt.id === beltId
            ? { ...belt, requirements: updatedRequirements }
            : belt,
        ),
      );

      // Notify other components to refresh
      window.dispatchEvent(new CustomEvent("beltSystemChanged"));

      toast({
        title: uiText("Requirement Deleted"),
        description: uiText("Requirement has been removed successfully."),
      });
    } catch (error) {
      console.error("Error deleting requirement:", error);
    }
  };

  // Group requirements by category
  const getRequirementsByCategory = (requirements: Requirement[]) => {
    const grouped: { [key: string]: Requirement[] } = {};

    requirements.forEach((req) => {
      if (!grouped[req.category]) {
        grouped[req.category] = [];
      }
      grouped[req.category].push(req);
    });

    return grouped;
  };

  // Get categories for current style
  const getCurrentCategories = () => {
    const beltSystem = getBeltSystem();
    if (beltSystem?.categories) {
      return beltSystem.categories;
    }
    return [
      uiText("Techniques"),
      uiText("Kata"),
      uiText("Sparring"),
      uiText("Self-Defense"),
      uiText("Philosophy"),
      uiText("Fitness"),
      uiText("Other"),
    ];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{uiText("Martial Arts Syllabus Manager")}</h1>
          <p className="text-muted-foreground">
            {uiText("Create and manage your dojo's belt requirements")}</p>
        </div>
        <Badge variant="outline" className="text-lg px-3 py-1">
          {beltLevels.length} {' '}{uiText("Belt Levels")}</Badge>
      </div>

      {/* Quick Actions */}
      {beltLevels.length > 0 && (
        <Alert>
          <Target className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>{uiText("You have")}{' '}{beltLevels.length} {' '}{uiText("belt levels configured")}</span>
            <div className="flex gap-2">
              <Button
                onClick={cleanupBeltSystem}
                variant="outline"
                size="sm"
                disabled={isLoading}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                {uiText("Clean & Reorder")}</Button>
              <Button
                onClick={forceResetBeltSystem}
                variant="destructive"
                size="sm"
                disabled={isLoading}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {uiText("Delete All")}</Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Error Helper */}
      <Alert className="border-amber-200 bg-amber-50">
        <Target className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-800">
          <div className="flex items-center justify-between">
            <div>
              <strong>{uiText("Getting constraint errors?")}</strong> {' '}{uiText("Use Force Reset to clear all belt data and resolve database conflicts.")}</div>
            <Button
              onClick={forceResetBeltSystem}
              variant="outline"
              size="sm"
              className="border-amber-300 text-amber-700 hover:bg-amber-100"
              disabled={isLoading}
            >
              {uiText("Force Reset")}</Button>
          </div>
        </AlertDescription>
      </Alert>

      <Tabs defaultValue="templates" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="templates">{uiText("Martial Arts Templates")}</TabsTrigger>
          <TabsTrigger value="markdown">{uiText("Custom Import")}</TabsTrigger>
          <TabsTrigger value="manual">{uiText("Manual Entry")}</TabsTrigger>
        </TabsList>

        {/* Templates Tab */}
        <TabsContent value="templates">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                {uiText("Quick Setup - Choose Your Martial Art & Style")}</CardTitle>
              <CardDescription>
                {uiText("Select a martial art and specific style to create a complete belt system instantly")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{uiText("Select Martial Art")}</Label>
                  <Select
                    value={selectedMartialArt}
                    onValueChange={(value) => {
                      setSelectedMartialArt(value);
                      setSelectedStyle(""); // Reset style when martial art changes
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={uiText("Choose a martial art...")} />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(MARTIAL_ARTS_DATABASE).map((martialArt) => (
                        <SelectItem key={martialArt} value={martialArt}>
                          {martialArt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{uiText("Select Style")}</Label>
                  <Select
                    value={selectedStyle}
                    onValueChange={setSelectedStyle}
                    disabled={!selectedMartialArt}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={uiText("Choose a style...")} />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableStyles().map((style) => (
                        <SelectItem key={style} value={style}>
                          {style}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {selectedMartialArt && selectedStyle && (
                <div className="p-4 bg-gray-50 rounded border">
                  <h4 className="font-medium">
                    {selectedMartialArt} - {selectedStyle}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {getBeltSystem()?.belts.length} {' '}{uiText("belt levels")}</p>
                  <p className="text-xs text-gray-500">
                    {uiText("Categories:")}{' '}{getBeltSystem()?.categories.join(", ")}
                  </p>
                </div>
              )}

              <Button
                onClick={createMartialArtSystem}
                disabled={isLoading || !selectedMartialArt || !selectedStyle}
                className="w-full bg-blue-600 hover:bg-blue-700"
                size="lg"
              >
                {isLoading ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Zap className="h-4 w-4 mr-2" />
                )}
                {uiText("Create")}{' '}{selectedMartialArt} {selectedStyle} {' '}{uiText("Belt System")}</Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custom Import Tab */}
        <TabsContent value="markdown">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {uiText("Custom Markdown Import")}</CardTitle>
              <CardDescription>
                {uiText("Import your existing syllabus in markdown format")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                value={markdownInput}
                onChange={(e) => setMarkdownInput(e.target.value)}
                placeholder={uiText("## No Kyu – WHITE BELT\n\n### Stances\n- Fudo Dachi – Formal Stance\n- Yoi Dachi – Ready Stance\n\n### Strikes\n- Seiken Jodan Tsuki – Closed Fist Upper Punch\n\n---\n\n## 10th Kyu – ORANGE BELT\n\n### Stances\n- Heiko Dachi – Parallel Stance")}
                rows={12}
                className="font-mono text-sm"
              />

              <Button
                onClick={handleMarkdownUpload}
                disabled={isLoading || !markdownInput.trim()}
                className="w-full"
              >
                {isLoading ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <FileText className="h-4 w-4 mr-2" />
                )}
                {uiText("Import Syllabus")}</Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Manual Entry Tab */}
        <TabsContent value="manual">
          <Card>
            <CardHeader>
              <CardTitle>{uiText("Add Individual Requirements")}</CardTitle>
              <CardDescription>
                {uiText("Add techniques to existing belt levels")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{uiText("Select Belt Level")}</Label>
                  <Select value={selectedBelt} onValueChange={setSelectedBelt}>
                    <SelectTrigger>
                      <SelectValue placeholder={uiText("Choose belt level...")} />
                    </SelectTrigger>
                    <SelectContent>
                      {beltLevels.map((belt) => (
                        <SelectItem key={belt.id} value={belt.id}>
                          {belt.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{uiText("Category")}</Label>
                  <Select
                    value={newRequirement.category}
                    onValueChange={(value) =>
                      setNewRequirement((prev) => ({
                        ...prev,
                        category: value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={uiText("Select category...")} />
                    </SelectTrigger>
                    <SelectContent>
                      {getCurrentCategories().map((category) => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{uiText("Technique/Requirement")}</Label>
                <Input
                  value={newRequirement.title}
                  onChange={(e) =>
                    setNewRequirement((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                  placeholder={uiText("e.g., Front Kick, Kata Heian Shodan, 10 Push-ups")}
                />
              </div>

              <div className="space-y-2">
                <Label>{uiText("Description (Optional)")}</Label>
                <Input
                  value={newRequirement.description}
                  onChange={(e) =>
                    setNewRequirement((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder={uiText("e.g., With proper form and technique")}
                />
              </div>

              <Button
                onClick={handleAddRequirement}
                disabled={
                  !selectedBelt ||
                  !newRequirement.title ||
                  !newRequirement.category
                }
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                {uiText("Add Requirement")}</Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Belt Levels Display */}
      {beltLevels.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">{uiText("Current Belt System")}</h2>

          {beltLevels.map((belt) => {
            const groupedRequirements = getRequirementsByCategory(
              belt.requirements,
            );
            const categories = Object.keys(groupedRequirements);

            return (
              <Card key={belt.id} className={`${belt.color} border-2`}>
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-xl">{belt.name}</CardTitle>
                      <Badge variant="outline" className="mt-2">
                        {belt.requirements.length} {' '}{uiText("requirements")}</Badge>
                    </div>
                    <div className="flex gap-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>{uiText("Edit")}{' '}{belt.name}</DialogTitle>
                            <DialogDescription>
                              {uiText("Add or remove requirements for this belt level")}</DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4">
                            {/* Add new requirement section */}
                            <div className="border rounded p-4 space-y-3">
                              <h4 className="font-medium">
                                {uiText("Add New Requirement")}</h4>
                              <div className="grid grid-cols-2 gap-3">
                                <Select
                                  value={newRequirement.category}
                                  onValueChange={(value) =>
                                    setNewRequirement((prev) => ({
                                      ...prev,
                                      category: value,
                                    }))
                                  }
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder={uiText("Category")} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {getCurrentCategories().map((category) => (
                                      <SelectItem
                                        key={category}
                                        value={category}
                                      >
                                        {category}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Input
                                  placeholder={uiText("Technique name")}
                                  value={newRequirement.title}
                                  onChange={(e) =>
                                    setNewRequirement((prev) => ({
                                      ...prev,
                                      title: e.target.value,
                                    }))
                                  }
                                />
                              </div>
                              <Input
                                placeholder={uiText("Description (optional)")}
                                value={newRequirement.description}
                                onChange={(e) =>
                                  setNewRequirement((prev) => ({
                                    ...prev,
                                    description: e.target.value,
                                  }))
                                }
                              />
                              <Button
                                onClick={() => {
                                  setSelectedBelt(belt.id);
                                  handleAddRequirement();
                                }}
                                disabled={
                                  !newRequirement.title ||
                                  !newRequirement.category
                                }
                                size="sm"
                              >
                                <Plus className="h-4 w-4 mr-2" />
                                {uiText("Add")}</Button>
                            </div>

                            {/* Current requirements */}
                            <div className="space-y-3">
                              <h4 className="font-medium">
                                {uiText("Current Requirements")}</h4>
                              {Object.entries(groupedRequirements).map(
                                ([category, reqs]) => (
                                  <div
                                    key={category}
                                    className="border rounded p-3"
                                  >
                                    <h5 className="font-medium text-sm mb-2">
                                      {category}
                                    </h5>
                                    <div className="space-y-2">
                                      {reqs.map((req) => (
                                        <div
                                          key={req.id}
                                          className="flex items-center justify-between bg-gray-50 p-2 rounded"
                                        >
                                          <div>
                                            <span className="font-medium text-sm">
                                              {req.title}
                                            </span>
                                            {req.description && (
                                              <p className="text-xs text-gray-600">
                                                {req.description}
                                              </p>
                                            )}
                                          </div>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 w-6 p-0 text-red-600 hover:text-red-800"
                                            onClick={() =>
                                              handleDeleteRequirement(
                                                belt.id,
                                                req.id,
                                              )
                                            }
                                          >
                                            <X className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>

                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-800 hover:bg-red-50"
                        onClick={() =>
                          handleDeleteBeltLevel(belt.id, belt.name)
                        }
                        disabled={isLoading}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {categories.length === 0 ? (
                    <p className="text-gray-500 italic">
                      {uiText("No requirements added yet - click Edit to add some!")}</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {categories.map((category) => (
                        <div key={category} className="space-y-3">
                          <h4 className="font-semibold text-base border-b pb-1">
                            {category}
                          </h4>
                          <div className="space-y-2">
                            {groupedRequirements[category].map(
                              (requirement) => (
                                <div
                                  key={requirement.id}
                                  className="flex items-start gap-3"
                                >
                                  <div className="flex-shrink-0 mt-1">
                                    <div className="w-4 h-4 border border-gray-400 rounded"></div>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm">
                                      {requirement.title}
                                    </div>
                                    {requirement.description && (
                                      <div className="text-xs text-gray-600 mt-1">
                                        {requirement.description}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {beltLevels.length === 0 && (
        <Card className="border-dashed border-2">
          <CardContent className="flex flex-col items-center justify-center p-12 text-center">
            <Target className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">
              {uiText("No Belt System Created")}</h3>
            <p className="text-gray-500 mb-6">
              {uiText("Choose a martial art and style from the templates above to get started.")}</p>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setSelectedMartialArt("Karate");
                  setSelectedStyle("Kyokushin");
                }}
                variant="outline"
              >
                {uiText("Try Kyokushin Karate")}</Button>
              <Button
                onClick={() => {
                  setSelectedMartialArt("Taekwondo");
                  setSelectedStyle("WTF/WT (World Taekwondo)");
                }}
                variant="outline"
              >
                {uiText("Try WTF Taekwondo")}</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Export with multiple names for compatibility
export default RefactoredAdminSyllabusManager;
export { RefactoredAdminSyllabusManager };
export const BeltManagement = RefactoredAdminSyllabusManager;
